/**
 * Small controls the Alert Actions screens share. Kept here rather than in common/ because
 * their styling is tuned to these forms (compact 13px inputs inside cards and modals).
 */
import type { ComponentType, ReactNode } from 'react';

export const aaInputCls =
  'mt-0.5 w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground placeholder:text-muted-foreground disabled:opacity-50';
export const aaLabelCls = 'text-[11px] font-medium text-muted-foreground';

/** On/off switch — the same look as the Alert Rules list's enable switch. */
export function AASwitch({
  checked,
  onChange,
  disabled,
  busy,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  busy?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled || busy}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-brand' : 'border border-border bg-muted'
      } ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'} ${busy ? 'opacity-60' : ''}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[1.15rem]' : 'translate-x-[0.15rem]'
        }`}
      />
    </button>
  );
}

/** Two-or-more choice segmented control (e.g. "Sent by: This site | Via Cloud"). */
export function AASegmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: { value: T; label: string; icon?: ComponentType<{ className?: string }>; hint?: string }[];
  onChange: (next: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
      {options.map((o) => {
        const active = o.value === value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            title={o.hint}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-[12px] transition-colors ${
              active ? 'bg-card font-medium text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            } ${disabled ? 'cursor-default opacity-60' : ''}`}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Card frame used by the channel cards and the cloud-relay card. */
export function AACard({
  icon: Icon,
  title,
  subtitle,
  right,
  children,
  footer,
}: {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="flex min-w-0 flex-col rounded-xl border border-border bg-card">
      <header className="flex items-start gap-3 border-b border-border px-4 py-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-semibold text-foreground">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{subtitle}</p>}
        </div>
        {right && <div className="flex shrink-0 items-center gap-2">{right}</div>}
      </header>
      {children && <div className="flex-1 px-4 py-3">{children}</div>}
      {footer && <footer className="border-t border-border px-4 py-3">{footer}</footer>}
    </section>
  );
}
